import { BrowserRouter, Routes, Route } from "react-router-dom";
import ReactDOM from "react-dom/client";

import SDK from "blocksdk";

import "@salesforce-ux/design-system/scss/index.scss";
import "./index.scss";

import {
    IconSettings,
    Settings as SLDSReactSettings,
} from "@salesforce/design-system-react";

import actionSprite from "@salesforce-ux/design-system/assets/icons/action-sprite/svg/symbols.svg";
import customSprite from "@salesforce-ux/design-system/assets/icons/custom-sprite/svg/symbols.svg";
import doctypeSprite from "@salesforce-ux/design-system/assets/icons/doctype-sprite/svg/symbols.svg";
import standardSprite from "@salesforce-ux/design-system/assets/icons/standard-sprite/svg/symbols.svg";
import utilitySprite from "@salesforce-ux/design-system/assets/icons/utility-sprite/svg/symbols.svg";

import App from "./App";

import { Settings } from "./routes/Settings";
import { Basic } from "./routes/settings/Basic";
import { Thumbnails } from "./routes/settings/Thumbnails";
import { Gifs } from "./routes/settings/Gifs";
import { EmbedForWeb } from "./routes/settings/EmbedForWeb";
import { VimeoAuth } from "./routes/VimeoAuth";
import { VimeoAuthComplete } from "./routes/VimeoAuthComplete";

import { EmbedInEmail } from "./views/EmbedInEmail/EmbedInEmail";
import { Home } from "./views/Home";

import { setContentBlockContext } from "./sessionStorage";
import { contentBlockPathPrefix } from "./common";
import { ThemeProvider } from "styled-components";

import { themes } from "@vimeo/iris/themes";

import { EmbedInEmailSource } from "./analytics/common";

// Without the following line, an error will be logged every time a modal is rendered.
// See https://react.lightningdesignsystem.com/components/modals/.
(SLDSReactSettings as any).setAppElement("#root");

const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
);

window.addEventListener("error", (err) => console.error("CAUGHT!", err));
window.addEventListener("unhandledrejection", (err) =>
    console.error("CAUGHT unhandledrejection!", err)
);

window.blockSdk = new SDK({
    tabs: ["htmlblock", "stylingblock"],
});

// When running the UI in localhost mode using its own server,
// the blocksdk will never complete the handshake with the
// parent window due to the difference in the port number.
// Therefore, none of the blocksdk methods will work. You
// should instead serve the UI using the API server which
// ensures that the UI and the API share the same host
// as well as port number and therefore the handshake will
// complete successfully allowing the app to communicate
// with Content Builder.
if (window.location.host.includes("localhost:3000")) {
    console.warn(
        "Vimeo SFMC: UI is running using a dev server. Embedding content in email templates will not work using the blocksdk. Use the API to serve the UI by setting REDIRECT_UI_TO_LOCALHOST to false in the API folder."
    );
}

setContentBlockContext(`/${contentBlockPathPrefix}`);

root.render(
    <ThemeProvider theme={themes.light}>
        <IconSettings
            actionSprite={actionSprite}
            customSprite={customSprite}
            doctypeSprite={doctypeSprite}
            standardSprite={standardSprite}
            utilitySprite={utilitySprite}
        >
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<App />}>
                        <Route index element={<Home />} />
                        <Route
                            path={`${contentBlockPathPrefix}/email`}
                            element={<Home />}
                        />
                        <Route
                            path={`${contentBlockPathPrefix}/web`}
                            element={<Home />}
                        />
                        <Route
                            path="videos/:videoId/embed-in-email"
                            element={
                                <EmbedInEmail
                                    source={EmbedInEmailSource.embedCta}
                                    useModal={false}
                                />
                            }
                        />
                    </Route>
                    <Route
                        path="/videos/:videoId/settings"
                        element={<Settings />}
                    >
                        <Route path="basic" element={<Basic />} />
                        <Route path="thumbnails" element={<Thumbnails />} />
                        <Route path="gifs" element={<Gifs />} />
                        <Route path="embed-for-web" element={<EmbedForWeb />} />
                    </Route>
                    <Route path="/vimeo-auth" element={<VimeoAuth />} />
                    <Route
                        path="/vimeo-auth-complete"
                        element={<VimeoAuthComplete />}
                    />
                </Routes>
            </BrowserRouter>
        </IconSettings>
    </ThemeProvider>
);
